"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { CheckoutDialog } from "@/components/checkout_dialog";
import { PricingCardArtistic } from "@/components/pricing/pricing_card_artistic";
import { highlightPricingTextFeatures } from "@/components/pricing/pricing_text_highlight";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@/components/ui/accordion";
import type { PricingRecord } from "@/lib/pricing-data";
import { findInitialCheckoutPlan, toCheckoutPlan } from "./checkout_launch";
import PriceMatchDialog from "./price_match_dialog";

export default function GrowthPricingTab({
	data,
	country = "usd",
	initialCheckoutPlanId = null,
}: {
	data: PricingRecord[];
	country?: string;
	initialCheckoutPlanId?: number | null;
}) {
	const [selectedPlan, setSelectedPlan] = useState<PricingRecord | null>(null);
	const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
	const openedInitialPlanIdRef = useRef<number | null>(null);

	const sortedData = useMemo(
		() =>
			[...(data ?? [])].sort((a, b) => {
				const priceA = country === "inr" ? a.price_in_inr : a.price_in_usd;
				const priceB = country === "inr" ? b.price_in_inr : b.price_in_usd;
				return priceA - priceB;
			}),
		[data, country],
	);

	const handleGetStarted = (plan: PricingRecord) => {
		setSelectedPlan(plan);
		setIsCheckoutOpen(true);
	};

	useEffect(() => {
		if (
			!initialCheckoutPlanId ||
			openedInitialPlanIdRef.current === initialCheckoutPlanId
		) {
			return;
		}

		const initialPlan = findInitialCheckoutPlan(
			sortedData,
			initialCheckoutPlanId,
		);
		if (!initialPlan) return;

		setSelectedPlan(initialPlan);
		setIsCheckoutOpen(true);
		openedInitialPlanIdRef.current = initialCheckoutPlanId;
	}, [sortedData, initialCheckoutPlanId]);

	return (
		<>
			<CheckoutDialog
				open={isCheckoutOpen}
				onOpenChange={setIsCheckoutOpen}
				plan={selectedPlan ? toCheckoutPlan(selectedPlan) : null}
				currency={country === "inr" ? "inr" : "usd"}
			/>

			<div className="w-full max-w-3xl mx-auto text-center mb-10">
				<p className="font-inter text-zinc-500 text-base leading-relaxed">
					Audience growth campaigns that bring real, interested viewers to your
					channel through targeted ads.
				</p>
			</div>

			<section className="w-full mb-12">
				<div
					className={`grid grid-cols-1 md:grid-cols-2 ${
						sortedData.length % 3 === 0 ? "lg:grid-cols-3" : "lg:grid-cols-4"
					} gap-6 max-w-7xl mx-auto`}
				>
					{sortedData.map((pkg, i) => (
						<div key={pkg.id} className="h-full">
							<PricingCardArtistic
								name={pkg.tier || pkg.name}
								price={country === "inr" ? pkg.price_in_inr : pkg.price_in_usd}
								currency={country}
								features={pkg.features}
								popular={pkg.popular}
								onGetStarted={() => handleGetStarted(pkg)}
								index={i}
								description="Audience Growth Campaign"
								categoryIcon="bell"
							/>
						</div>
					))}
				</div>
			</section>

			<PriceMatchDialog />

			<div className="w-full max-w-3xl mx-auto mb-20">
				<div className="text-center mb-12">
					<h2 className="font-syne font-black text-3xl uppercase tracking-tight text-zinc-900">
						Growth Campaign FAQ
					</h2>
					<div className="h-1 w-12 bg-zinc-200 mx-auto mt-4 rounded-full" />
				</div>

				<Accordion type="single" collapsible className="w-full space-y-4">
					<AccordionItem
						value="item-1"
						className="border border-zinc-200 rounded-2xl px-4 overflow-hidden bg-white/50 hover:bg-white transition-colors"
					>
						<AccordionTrigger className="font-syne font-bold text-lg text-zinc-700 hover:text-zinc-900 hover:no-underline py-6">
							Audience Quality
						</AccordionTrigger>
						<AccordionContent className="font-inter text-zinc-500 leading-relaxed pb-6">
							{highlightPricingTextFeatures(
								"We focus on attracting genuinely interested viewers through precision ad targeting. Our campaigns reach audiences with high affinity for your content niche.",
							)}
						</AccordionContent>
					</AccordionItem>

					<AccordionItem
						value="item-2"
						className="border border-zinc-200 rounded-2xl px-4 overflow-hidden bg-white/50 hover:bg-white transition-colors"
					>
						<AccordionTrigger className="font-syne font-bold text-lg text-zinc-700 hover:text-zinc-900 hover:no-underline py-6">
							Delivery Speed
						</AccordionTrigger>
						<AccordionContent className="font-inter text-zinc-500 leading-relaxed pb-6">
							{highlightPricingTextFeatures(
								"Most campaigns start delivering within 24-48 hours. Larger campaigns are paced over time for steady, sustainable audience growth.",
							)}
						</AccordionContent>
					</AccordionItem>

					<AccordionItem
						value="item-3"
						className="border border-zinc-200 rounded-2xl px-4 overflow-hidden bg-white/50 hover:bg-white transition-colors"
					>
						<AccordionTrigger className="font-syne font-bold text-lg text-zinc-700 hover:text-zinc-900 hover:no-underline py-6">
							How It Works
						</AccordionTrigger>
						<AccordionContent className="font-inter text-zinc-500 leading-relaxed pb-6">
							{highlightPricingTextFeatures(
								"All audience growth comes from targeted Google Video Ad campaigns reaching real people. We connect your content with viewers who are genuinely interested in your niche.",
							)}
						</AccordionContent>
					</AccordionItem>
				</Accordion>
			</div>
		</>
	);
}
